import {isEmpty} from 'lodash';
import {rbacDepartments} from '../../constants/roleBasedAccessControlConstants';
import {
  RbacDepartment,
  RbacPermissionCheckParams,
  RbacPermissionString,
} from '../../types/typeRoleBasedAccessControls';
import {SidebarItemsType} from '../../types/typeSidebar';
import {USER_TYPE} from '../../constants/UserType';
import {envRole} from '..';

/**
 * Check if the user has the required permission
 * @param userAccessPermissions - The user's permissions
 * @param module - The module of the permission
 * @param resource - The resource of the permission
 * @param operation - The operation of the permission
 * @returns true if the user has the required permission, false otherwise
 */
export const checkIfUserIsRbacPermissible = (
  userAccessPermissions: RbacPermissionString[],
  userAccessRoleDepartments: RbacDepartment[],
  {permissionString, module, resource, operation}: RbacPermissionCheckParams
) => {
  if (!userAccessPermissions) return false;

  const localPermission: RbacPermissionString | RbacPermissionString[] =
    permissionString || `${module}:${resource}:${operation}`;

  const hasRoleSuperAdmin = userAccessRoleDepartments?.includes(
    rbacDepartments.SUPER_ADMIN
  );
  const isIPPortal = +envRole === USER_TYPE.MERCHANT;
  // Check and pass checkRbac if:
  // - Current env is IP Portal (rbac check should apply for Admin Portal only)
  // - User has super admin role
  if (isIPPortal || hasRoleSuperAdmin) return true;

  // Check if the permission is an array;
  // If it is, check if the user has any of the permissions in the array
  if (Array.isArray(localPermission)) {
    // If the permission is an empty array, treat it as all-permissions allowed
    if (isEmpty(localPermission)) return true;

    return userAccessPermissions.some(permission =>
      localPermission.includes(permission)
    );
  }

  // If it is not an array, check if the user has the permission
  return userAccessPermissions.includes(localPermission);
};

/**
 * Filters sidebar navigation items based on user's RBAC permissions and departments
 *
 * @param {Object} params - The parameters object
 * @param {SidebarItemsType[]} params.sidebarItems - Array of sidebar menu items to filter
 * @param {RbacPermissionString[]} params.userAccessPermissions - User's permission strings
 * @param {RbacDepartment[]} params.userRoleDepartments - User's department roles
 *
 * @returns {Object} An object containing:
 *   - filteredItems: The filtered sidebar items
 *   - suggestedRoute: A recommended default route based on the first accessible item
 */
export const rbacAccessibleOnlyRoutes = ({
  sidebarItems,
  userAccessPermissions,
  userRoleDepartments,
}: {
  sidebarItems: SidebarItemsType[];
  userAccessPermissions: RbacPermissionString[];
  userRoleDepartments: RbacDepartment[];
}) => {
  // Filter sidebar items recursively based on RBAC permissions
  const filterItemsByPermissions = (
    items: SidebarItemsType[]
  ): SidebarItemsType[] => {
    if (!items || !items.length) return [];

    return items
      .map(item => {
        // Check if the current item is accessible based on permissions
        const isItemAccessible = checkIfUserIsRbacPermissible(
          userAccessPermissions,
          userRoleDepartments,
          {permissionString: item.requiredPermissions}
        );

        if (!isItemAccessible) return null;

        // Create a copy of the item to avoid mutating the original
        const filteredItem = {...item};

        // If the item has children, filter them recursively
        if (filteredItem.children && filteredItem.children.length > 0) {
          const filteredChildren = filterItemsByPermissions(
            filteredItem.children
          );

          // If all children are filtered out, remove the children property
          if (filteredChildren.length === 0) {
            delete filteredItem.children;
            // If the item was only a subPage because of children, remove that property too
            if (filteredItem.subPage) {
              delete filteredItem.subPage;
            }
          } else {
            filteredItem.children = filteredChildren;
          }
        }

        return filteredItem;
      })
      .filter(Boolean) as SidebarItemsType[]; // Filter out null items
  };

  // Apply the filtering
  const filteredItems = filterItemsByPermissions(sidebarItems);

  return {
    filteredItems,
    suggestedRoute: findFirstAvailableRoute(filteredItems, {
      accessPermissions: userAccessPermissions,
      roleDepartments: userRoleDepartments,
    }),
  };
};

/**
 * Permission checking by analyzing parent-child permission relationships and finding the most appropriate route
 * @param items - Array of sidebar items to search through
 * @param accessPermissions - User's permission strings
 * @param roleDepartments - User's department roles
 * @returns The most appropriate available href found or undefined if none exists
 */
export const findFirstAvailableRoute = (
  items: SidebarItemsType[],
  {
    accessPermissions,
    roleDepartments,
  }: {
    accessPermissions: RbacPermissionString[];
    roleDepartments: RbacDepartment[];
  }
): string | undefined => {
  if (!items || items.length === 0) return undefined;

  // Helper function to extract all unique required permissions from an item and its children
  const extractAllRequiredPermissions = (item: SidebarItemsType): string[] => {
    // Start with the current item's permissions
    const permissions: string[] = [];

    // Add current item's permissions if they exist
    if (item.requiredPermissions && Array.isArray(item.requiredPermissions)) {
      permissions.push(...item.requiredPermissions);
    }

    // Recursively add permissions from children
    if (item.children && item.children.length > 0) {
      item.children.forEach(child => {
        const childPermissions = extractAllRequiredPermissions(child);
        permissions.push(...childPermissions);
      });
    }

    // Return unique permissions only
    return [...new Set(permissions)];
  };

  // Loop through all items on the top level
  for (const item of items) {
    // Skip items without href
    if (!item.href) continue;

    // For each item in the top level, recursively check into all children within it
    // and obtain a unique list of requiredPermission items
    const allChildrenPermissions: string[] = [];

    if (item.children && item.children.length > 0) {
      item.children.forEach(child => {
        const childPermissions = extractAllRequiredPermissions(child);
        allChildrenPermissions.push(...childPermissions);
      });
    }

    // Create a unique list of all children permissions
    const uniqueChildrenPermissions = [...new Set(allChildrenPermissions)];

    // Get the current item's required permissions
    const itemPermissions = item.requiredPermissions || [];

    // Create parentExclusiveRequiredPermissions by removing child permissions from parent
    const parentExclusiveRequiredPermissions = itemPermissions.filter(
      permission => !uniqueChildrenPermissions.includes(permission)
    );

    // If parent has no unique permissions (all are covered by children)
    // or if user has the parent's unique permissions, return this route
    if (
      item.subPage === true &&
      (parentExclusiveRequiredPermissions.length === 0 ||
        parentExclusiveRequiredPermissions.some(permission =>
          accessPermissions.includes(permission)
        ))
    ) {
      return item.href;
    }

    // Check if any direct child has an accessible route
    if (item.children && item.children.length > 0) {
      const childRoute = findFirstAvailableRoute(item.children, {
        accessPermissions,
        roleDepartments,
      });

      if (childRoute) {
        return childRoute;
      }
    }
  }

  // If no route found through permission optimization, fall back to first item with href
  const firstItemWithHref = items.find(item => item.href);
  return firstItemWithHref?.href;
};
